import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, FileText, Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface UploadPolicyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const UploadPolicyModal = ({ open, onOpenChange }: UploadPolicyModalProps) => {
  const { toast } = useToast();

  const handleSingleUpload = () => {
    toast({
      title: "Upload Successful",
      description: "Policy has been uploaded and will be processed shortly.",
    });
    onOpenChange(false);
  };

  const handleBulkUpload = () => {
    toast({
      title: "Bulk Upload Initiated",
      description: "Multiple policies are being processed. You'll be notified when complete.",
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={true}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload Policy Documents</DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="single" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="single">Single Upload</TabsTrigger>
            <TabsTrigger value="bulk">Bulk Import</TabsTrigger>
          </TabsList>
          
          <TabsContent value="single" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Upload Single Policy
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="policy-file">Policy Document</Label>
                  <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                    <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Drag and drop your policy file here</p>
                      <p className="text-xs text-muted-foreground">Supports PDF, DOCX, TXT files up to 10MB</p>
                      <Button variant="outline" size="sm">
                        Browse Files
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Input placeholder="e.g., Security, HR, Finance" />
                  </div>
                  <div className="space-y-2">
                    <Label>Version</Label>
                    <Input placeholder="e.g., 1.0" />
                  </div>
                </div>
                
                <Button onClick={handleSingleUpload} className="w-full">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Policy
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="bulk" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Bulk Import Policies
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-muted p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Bulk Import Requirements:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• ZIP file containing multiple policy documents</li>
                    <li>• Include metadata.csv file for policy information</li>
                    <li>• Maximum 50 policies per upload</li>
                    <li>• Each file must be under 10MB</li>
                  </ul>
                </div>
                
                <div className="space-y-4">
                  <Button variant="outline" className="w-full">
                    <Download className="w-4 h-4 mr-2" />
                    Download Template CSV
                  </Button>
                  
                  <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                    <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Upload your ZIP file here</p>
                      <p className="text-xs text-muted-foreground">ZIP files up to 100MB supported</p>
                      <Button variant="outline" size="sm">
                        Browse Files
                      </Button>
                    </div>
                  </div>
                </div>
                
                <Button onClick={handleBulkUpload} className="w-full">
                  <Upload className="w-4 h-4 mr-2" />
                  Start Bulk Import
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};